import React, { useCallback, useMemo } from "react";
import { SubmitHandler, useForm, useFormState } from "react-hook-form";

import {
  Field,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  ToneSegments,
  TwoColumnRow,
} from "../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
  SelectForm,
  SelectOptionTypes,
} from "../../../../components/Input";
import { NetPreview } from "../../../../components/NetPreview";
import { NetToGrossHelper } from "../../../../components/NetToGrossHelper";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { DateFormat, formatCurrency, formatDate } from "../../../../utils";
import { useAccountOptions } from "../../../../hooks/useAccountOptions";
import { useNetAmountWhileTaxHidden } from "../../../../hooks/useNetAmountWhileTaxHidden";
import { useSubCategoryResetOnCategoryChange } from "../../../../hooks/useSubCategoryResetOnCategoryChange";
import { Category, TransactionTypeOptions } from "../../../Transactions";
import {
  amountLabelOf,
  clearedWhenBlank,
  OPTIONAL_TAX_WITHHELD_RULES,
} from "../../../../utils/taxWithheld";
import {
  AMOUNT_RULES,
  createDefaultProgress,
  FORM_ID,
  SEGMENT_LABELS,
  TONE_BY_TYPE,
  TYPE_OPTIONS,
} from "./constants";
import { MobileProgressForm } from "./MobileProgressForm";
import { ProgressObject, ProgressPanelProps } from "./types";

const useTransactionForm = (
  accountId: number,
  accountName: string,
  progress?: ProgressObject,
) => {
  // Initialize the form with either the provided progress or default values
  // the default progress transactionType is set to TYPE_OPTIONS[0] which is "Earnings" so we define the toAccount with the current accountId and accountName
  const startValue = progress ?? {
    ...createDefaultProgress(),
    toAccount: { value: String(accountId), label: accountName },
  };

  const form = useForm<ProgressObject>({
    defaultValues: startValue,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { control, setValue, watch } = form;
  const { isSubmitting } = useFormState({ control });

  const watchedValues = watch(["transactionType", "category"]);
  const [transactionType, category] = watchedValues;
  const prevTransactionType = React.useRef(transactionType?.value);

  // this needs to be tested but it seems to be working! ALSO NEED TO UPDATE THE SAME THING FOR THE INVESTMENT PANEL!!!!!
  React.useEffect(() => {
    if (!transactionType) return;

    // only run when transactionType actually changes
    if (transactionType.value !== prevTransactionType.current) {
      if (
        transactionType.value === TransactionTypeOptions.TRANSFER_IN ||
        transactionType.value === TransactionTypeOptions.INCOME
      ) {
        setValue("toAccount", { value: String(accountId), label: accountName });
        setValue("fromAccount", null);
      } else if (
        transactionType.value === TransactionTypeOptions.TRANSFER_OUT
      ) {
        setValue("fromAccount", {
          value: String(accountId),
          label: accountName,
        });
        setValue("toAccount", null);
      }
      setValue("taxWithheld", "");
      // update the ref
      prevTransactionType.current = transactionType.value;
    }
  }, [transactionType, accountId, accountName, setValue]);

  useSubCategoryResetOnCategoryChange(category?.value, setValue);

  return {
    form,
    startValue,
    isSubmitting,
    transactionType,
    category,
  };
};

export const ProgressPanel = ({
  title,
  progress,
  accountId,
  accountName,
  isOpen,
  onClose,
  handleOnSubmit,
  incomeCategoryList,
  expenseCategoryList,
}: ProgressPanelProps) => {
  const { accountOptions, load } = useAccountOptions({ excludeId: accountId });

  const { form, startValue, isSubmitting, transactionType, category } =
    useTransactionForm(accountId, accountName, progress);
  const { control, register, setValue, clearErrors, getValues } = form;

  const handleTypeChange = useCallback(
    (option: SelectOptionTypes) => {
      setValue("transactionType", option, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  // Memoized validation rules
  const validationRules = useMemo(
    () => ({
      amount: AMOUNT_RULES,
      taxWithheld: OPTIONAL_TAX_WITHHELD_RULES,
      transactionType: {
        required: "Transaction type is required",
      },
      fromAccount: {
        required: "From account is required",
      },
      toAccount: {
        required: "To account is required",
      },
      transactionDate: {
        required: "Date is required",
      },
      note: {
        minLength: {
          value: 3,
          message: "Description must be at least 3 characters",
        },
        maxLength: {
          value: 255,
          message: "Description cannot exceed 255 characters",
        },
      },
    }),
    [],
  );

  // Memoized category options based on transaction type
  const categoryOptions = useMemo(() => {
    switch (transactionType.value) {
      case TransactionTypeOptions.INCOME:
        return incomeCategoryList;
      case TransactionTypeOptions.EXPENSE:
        return expenseCategoryList;
      case TransactionTypeOptions.LOAN_PAYMENT:
      default:
        return [];
    }
  }, [transactionType, incomeCategoryList, expenseCategoryList]);

  // Memoized subcategory options
  const subCategoryOptions = useMemo(() => {
    if (transactionType.value === TransactionTypeOptions.TRANSFER) {
      return [];
    }
    const categorySelected: Category | undefined = categoryOptions.find(
      (c) => c.value === category?.value,
    );
    return categorySelected?.subCategories || [];
  }, [category, categoryOptions, transactionType]);

  const shouldShowTax = transactionType.value === TransactionTypeOptions.INCOME;

  // A blank tax on an earning goes out as null, so an edit clears the stored tax instead of keeping it.
  const onSubmit: SubmitHandler<ProgressObject> = useCallback(
    (data) => {
      const taxWithheld = shouldShowTax
        ? clearedWhenBlank(data.taxWithheld)
        : data.taxWithheld;
      return handleOnSubmit({ ...data, taxWithheld });
    },
    [handleOnSubmit, shouldShowTax],
  );

  const shouldShowFromAccount =
    transactionType.value === TransactionTypeOptions.TRANSFER_IN;
  const shouldShowToAccount =
    transactionType.value === TransactionTypeOptions.TRANSFER_OUT;
  const shouldShowCategory =
    categoryOptions.length > 0 &&
    transactionType.value !== TransactionTypeOptions.TRANSFER;
  const shouldShowSubCategory = subCategoryOptions.length > 0;
  useNetAmountWhileTaxHidden(shouldShowTax, progress, getValues, setValue);

  const isEditProgress = Boolean(progress);
  const isOutflow =
    transactionType.value === TransactionTypeOptions.TRANSFER_OUT;

  const buildDiscardDetails = (): ConfirmDetail => {
    const formValues = getValues();
    const amountNumber = Number.parseFloat(formValues.amount);
    const hasAmount = !Number.isNaN(amountNumber) && amountNumber > 0;
    const signedAmount = isOutflow ? -amountNumber : amountNumber;
    const formattedAmount = hasAmount
      ? formatCurrency(signedAmount)
      : undefined;
    const categoryLabel = formValues.category?.label;

    if (isEditProgress) {
      return {
        pill: {
          label: transactionType.label.toUpperCase(),
          variant: isOutflow ? "EXPENSE" : "INCOME",
        },
        title: categoryLabel ?? transactionType.label,
        subtitle: joinDetailParts(
          formValues.subCategory?.label,
          formatDate(formValues.transactionDate, DateFormat.DMY_SPACE),
        ),
        amount: formattedAmount,
        amountVariant: TONE_BY_TYPE[transactionType.value],
      };
    }

    return buildDraftDetails({
      noun: "progress",
      fieldValues: [
        formValues.amount,
        shouldShowTax ? formValues.taxWithheld : null,
        formValues.category,
        formValues.subCategory,
        formValues.fromAccount,
        formValues.toAccount,
        formValues.transactionDate,
        formValues.note,
      ],
      amount: formattedAmount,
      primaryLabel: categoryLabel,
    });
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Log savings progress"
      eyebrow="SAVINGS PROGRESS"
      title={title}
      form={form}
      resetValues={startValue}
      formId={FORM_ID}
      onSubmit={onSubmit}
      submitLabel="Save"
      discard={{
        title: "Discard your changes?",
        details: buildDiscardDetails,
      }}
      load={load}
      mobile={{
        title: "Add Progress",
        submitLabel: "Save",
        fields: (
          <MobileProgressForm
            control={control}
            register={register}
            setValue={setValue}
            clearErrors={clearErrors}
            transactionType={transactionType}
            accountOptions={accountOptions}
            categoryOptions={categoryOptions}
            subCategoryOptions={subCategoryOptions}
            shouldShowCategory={shouldShowCategory}
            shouldShowSubCategory={shouldShowSubCategory}
            shouldShowFromAccount={shouldShowFromAccount}
            shouldShowToAccount={shouldShowToAccount}
            shouldShowTax={shouldShowTax}
            isSubmitting={isSubmitting}
            onTypeChange={handleTypeChange}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Type *</FieldLabel>
          </LabelRow>
          <ToneSegments
            groupLabel="Type"
            options={TYPE_OPTIONS}
            toneByValue={TONE_BY_TYPE}
            labelByValue={SEGMENT_LABELS}
            selectedValue={transactionType.value}
            disabled={isSubmitting}
            onChange={handleTypeChange}
          />
        </Field>

        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="savings-progress-date">Date *</FieldLabel>
            </LabelRow>
            <InputDate
              id="savings-progress-date"
              rules={validationRules.transactionDate}
              name="transactionDate"
              control={control}
              placeholder="YYYY/MM/DD"
              maxDate={new Date()}
              disabled={isSubmitting}
            />
          </Field>

          <Field>
            <LabelRow>
              <FieldLabel htmlFor="savings-progress-amount">
                {amountLabelOf(shouldShowTax, { required: true })}
              </FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="savings-progress-amount"
              required
              rules={validationRules.amount}
              name="amount"
              control={control}
              disabled={isSubmitting}
              placeholder="Enter Amount"
              autoFocus={true}
            />
          </Field>
        </TwoColumnRow>

        {shouldShowTax && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="savings-progress-tax">
                Tax withheld
              </FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="savings-progress-tax"
              rules={validationRules.taxWithheld}
              name="taxWithheld"
              control={control}
              disabled={isSubmitting}
              placeholder="Enter Tax Withheld"
            />
            <NetPreview control={control} />
            <NetToGrossHelper setValue={setValue} disabled={isSubmitting} />
          </Field>
        )}
      </FormCard>

      <FormCard>
        {shouldShowCategory && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="category-select">Category</FieldLabel>
            </LabelRow>
            <SelectForm
              testId="category-select"
              {...register("category")}
              required={false}
              size="small"
              name="category"
              defaultValue={null}
              control={control}
              options={categoryOptions}
              disabled={isSubmitting}
            />
          </Field>
        )}

        {shouldShowSubCategory && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="subcategory-select">Subcategory</FieldLabel>
            </LabelRow>
            <SelectForm
              testId="subcategory-select"
              name="subCategory"
              required={false}
              size="small"
              control={control}
              defaultValue={null}
              options={subCategoryOptions}
              disabled={isSubmitting}
            />
          </Field>
        )}

        {shouldShowFromAccount && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="savings-progress-from-account">
                From account *
              </FieldLabel>
            </LabelRow>
            <SelectForm
              id="savings-progress-from-account"
              key="fromAccount"
              rules={validationRules.fromAccount}
              size="small"
              defaultValue={progress?.fromAccount ?? null}
              name="fromAccount"
              control={control}
              options={accountOptions}
              disabled={isSubmitting}
            />
          </Field>
        )}

        {shouldShowToAccount && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="savings-progress-to-account">
                To account *
              </FieldLabel>
            </LabelRow>
            <SelectForm
              id="savings-progress-to-account"
              key="toAccount"
              rules={validationRules.toAccount}
              size="small"
              defaultValue={progress?.toAccount ?? null}
              name="toAccount"
              control={control}
              options={accountOptions}
              disabled={isSubmitting}
            />
          </Field>
        )}

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="savings-progress-note">Description</FieldLabel>
          </LabelRow>
          <FormInput
            id="savings-progress-note"
            rules={validationRules.note}
            name="note"
            control={control}
            placeholder="Enter a description"
            handleClear={false}
            disabled={isSubmitting}
          />
        </Field>
      </FormCard>
    </FormPanel>
  );
};
